import React, {useEffect, useState, forwardRef} from 'react';
import {StatesButton, StatesButtonStates, TextField, ThreeDotsLoader} from '@wix/wix-ui-tpa/cssVars';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {classes} from './CodeInput.st.css';
import {SPECS} from '../../../../../common/constants';

export enum CodesInputDataHooks {
  TextInput = 'CodesInput.TextInput',
  Button = 'CodesInput.Button',
  ThreeDotsLoader = 'CodesInput.ThreeDotsLoader',
}

export interface CodeInputProps {
  dataHook?: string;
  placeholder?: string;
  appliedValue?: string;
  isLoading: boolean;
  applyText: string;
  removeText: string;
  onApply: (code: string) => void;
  onRemove: () => void;
  error?: string;
  isUpdating?: boolean;
  ariaLabel?: string;
}

export const CodeInput = forwardRef<HTMLInputElement, CodeInputProps>(
  (
    {
      dataHook,
      placeholder,
      appliedValue,
      isLoading,
      applyText,
      removeText,
      onApply,
      onRemove,
      error,
      isUpdating,
      ariaLabel,
    }: CodeInputProps,
    ref
    // eslint-disable-next-line sonarjs/cognitive-complexity
  ) => {
    const {experiments} = useExperiments();
    const [value, setValue] = useState(appliedValue ?? '');
    const [buttonState, setButtonState] = useState(StatesButtonStates.IDLE);

    useEffect(() => {
      setValue(appliedValue ?? '');
    }, [appliedValue]);

    const onButtonClick = () => {
      const isRemove = !!appliedValue;

      if (isRemove) {
        onRemove();
        /* istanbul ignore if */
        if (value !== appliedValue) {
          setValue('');
        }
      } else {
        onApply(value.trim());
      }
    };

    useEffect(
      () => {
        if (isUpdating || (isLoading && !!appliedValue)) {
          setButtonState(StatesButtonStates.IN_PROGRESS);
          return;
        }
        setButtonState(StatesButtonStates.IDLE);
      },
      /* eslint-disable react-hooks/exhaustive-deps*/ [isUpdating, isLoading]
    );

    return (
      <div className={classes.container} data-hook={dataHook}>
        <TextField
          data-hook={CodesInputDataHooks.TextInput}
          className={
            experiments.enabled(SPECS.FixAutoZoomInputOnMobile)
              ? classes.textField
              : /* istanbul ignore next */ classes.textFieldOld
          }
          placeholder={placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => setValue((prev) => prev.trim())}
          successIcon
          success={!!appliedValue && !error && !isUpdating}
          disabled={!!appliedValue && !error && !isUpdating}
          error={!!error}
          errorMessage={error}
          newErrorMessage
          inputRef={(input: HTMLInputElement) => {
            (ref as React.MutableRefObject<HTMLInputElement | null>).current = input;
          }}
          aria-label={ariaLabel}
        />
        <StatesButton
          className={classes.statesButton}
          data-hook={CodesInputDataHooks.Button}
          idleContent={appliedValue ? removeText : applyText}
          state={buttonState}
          disabled={!value.trim()}
          onClick={onButtonClick}
          inProgressContent={
            <ThreeDotsLoader data-hook={CodesInputDataHooks.ThreeDotsLoader} className={classes.threeDotButton} />
          }
          upgrade={true}
        />
      </div>
    );
  }
);

CodeInput.displayName = 'CodeInput';
